import React, { useEffect, useMemo, useState } from 'react';
import { cryptoApi } from '../api/botApiCrypto';

const DEFAULT_WATCHLIST = 'BTC-USD, ETH-USD, SOL-USD, XRP-USD, DOGE-USD, ADA-USD, AVAX-USD, LINK-USD, LTC-USD, BCH-USD';

function asList(text) {
  return String(text || '')
    .replace(/\n/g, ',')
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);
}

function Row({ label, children }) {
  return (
    <label className="block text-sm text-gray-200 mb-3">
      <span className="block mb-1 text-gray-400">{label}</span>
      {children}
    </label>
  );
}

function inputClass() {
  return 'w-full rounded-lg bg-gray-950 border border-yellow-700/40 px-3 py-2 text-white outline-none focus:border-yellow-400';
}

function getUniverseMode(settings) {
  if (settings?.universe_mode === 'watchlist_only') return 'watchlist_only';
  if (settings?.universe_mode === 'robinhood_all') return 'robinhood_all';
  return settings?.scan_all === false ? 'watchlist_only' : 'robinhood_all';
}

function getUniverseDetails(payload) {
  if (!payload || typeof payload !== 'object') return {};
  if (payload.details && typeof payload.details === 'object') return payload.details;
  if (payload.universe && !Array.isArray(payload.universe) && typeof payload.universe === 'object') return payload.universe;
  return payload;
}

function getUniverseSymbols(payload) {
  const details = getUniverseDetails(payload);
  const candidates = [
    details.symbols,
    details.pairs,
    details.tradable_pairs,
    details.eligible_pairs,
    Array.isArray(details.universe) ? details.universe : null,
    details.items,
  ];
  const raw = candidates.find((value) => Array.isArray(value)) || [];
  return raw
    .map((item) => {
      if (typeof item === 'string') return item;
      return item?.symbol || item?.pair || item?.id || '';
    })
    .map((value) => String(value || '').toUpperCase())
    .filter(Boolean);
}

export default function CryptoBotPanel() {
  const [settings, setSettings] = useState(null);
  const [targetSettings, setTargetSettings] = useState(null);
  const [watchlistText, setWatchlistText] = useState(DEFAULT_WATCHLIST);
  const [top10, setTop10] = useState([]);
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState(null);
  const [universe, setUniverse] = useState(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [rhApiKey, setRhApiKey] = useState('');
  const [rhPrivateKey, setRhPrivateKey] = useState('');

  async function refreshUniverse() {
    try {
      const result = await cryptoApi.getUniverse();
      setUniverse(result || null);
      return result;
    } catch (e) {
      // Universe diagnostics are useful but should never prevent the Crypto panel from loading.
      setUniverse({ error: e.message || 'Could not load the live Robinhood universe.' });
      return null;
    }
  }

  async function refresh() {
    const [s, ts, l, st] = await Promise.all([
      cryptoApi.getSettings(),
      cryptoApi.getTargetSettings(),
      cryptoApi.logs(),
      cryptoApi.status(),
    ]);
    setSettings(s.settings);
    setTargetSettings(ts.settings);
    setWatchlistText((s.settings?.watchlist || []).join(', ') || DEFAULT_WATCHLIST);
    setLogs(l.logs || []);
    setStatus(st);
    await refreshUniverse();
  }

  useEffect(() => {
    refresh().catch((e) => setMessage(e.message));
  }, []);

  const states = useMemo(() => status?.states || [], [status]);
  const universeMode = getUniverseMode(settings);
  const universeDetails = useMemo(() => getUniverseDetails(universe), [universe]);
  const universeSymbols = useMemo(() => getUniverseSymbols(universe), [universe]);
  const universeCount =
    universeDetails?.eligible_count ??
    universeDetails?.tradable_count ??
    universeDetails?.count ??
    universeSymbols.length;
  const universeSource =
    universeDetails?.source ||
    universeDetails?.universe_source ||
    (universeMode === 'robinhood_all' ? 'Robinhood live trading pairs' : 'Crypto Watchlist');
  const universeFallback = Boolean(
    universeDetails?.fallback_used ??
    universeDetails?.used_fallback ??
    universeDetails?.fallback
  );

  async function wrap(fn, okText) {
    setBusy(true);
    setMessage('');
    try {
      const result = await fn();
      setMessage(okText || result.message || 'Done');
      await refresh();
      return result;
    } catch (e) {
      setMessage(e.message || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  if (!settings || !targetSettings) {
    return (
      <div className="rounded-xl border border-yellow-700/40 p-4 text-yellow-300">
        <div>Loading crypto bots...</div>
        {message ? (
          <div className="mt-3 rounded-lg border border-red-700/50 bg-red-950/30 p-3 text-sm text-red-200">
            Crypto API error: {message}
          </div>
        ) : (
          <div className="mt-2 text-xs text-gray-400">Checking crypto settings, target settings, logs, and status…</div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-yellow-700/40 bg-black/60 p-5 shadow-lg shadow-yellow-900/10">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-2xl font-bold text-yellow-300">Crypto Market Bot</h2>
            <p className="text-sm text-gray-400">Scans the crypto market, buys the best setup, then hands it to crypto auto exits.</p>
          </div>
          <button
            disabled={busy}
            onClick={() => wrap(() => cryptoApi.top10().then((r) => { setTop10(r.candidates || []); return r; }), 'Top 10 refreshed')}
            className="rounded-lg border border-yellow-500 px-3 py-2 text-yellow-200 hover:bg-yellow-500/10 disabled:opacity-50"
          >
            Refresh Top 10
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <Row label="Enabled">
            <select className={inputClass()} value={settings.enabled ? '1' : '0'} onChange={(e) => setSettings({ ...settings, enabled: Number(e.target.value) })}>
              <option value="0">Off</option>
              <option value="1">On</option>
            </select>
          </Row>
          <Row label="Broker">
            <select className={inputClass()} value={settings.broker || 'paper'} onChange={(e) => setSettings({ ...settings, broker: e.target.value })}>
              <option value="paper">Paper / Test</option>
              <option value="robinhood">Robinhood Crypto</option>
            </select>
          </Row>
          <Row label="Risk Level">
            <select className={inputClass()} value={settings.risk_level || 'balanced'} onChange={(e) => setSettings({ ...settings, risk_level: e.target.value })}>
              <option value="conservative">Conservative</option>
              <option value="balanced">Balanced</option>
              <option value="aggressive">Aggressive</option>
            </select>
          </Row>
          <Row label="Paper Mode">
            <select className={inputClass()} value={settings.paper_mode ? '1' : '0'} onChange={(e) => setSettings({ ...settings, paper_mode: Number(e.target.value) })}>
              <option value="1">Paper only</option>
              <option value="0">Live capable</option>
            </select>
          </Row>
          <Row label="Allow Live Orders">
            <select className={inputClass()} value={settings.allow_live ? '1' : '0'} onChange={(e) => setSettings({ ...settings, allow_live: Number(e.target.value) })}>
              <option value="0">No</option>
              <option value="1">Yes</option>
            </select>
          </Row>
          <Row label="Max Trade $">
            <input className={inputClass()} type="number" value={settings.max_trade_usd || 25} onChange={(e) => setSettings({ ...settings, max_trade_usd: Number(e.target.value) })} />
          </Row>
          <Row label="Min Buy Score">
            <input className={inputClass()} type="number" value={settings.min_score || 58} onChange={(e) => setSettings({ ...settings, min_score: Number(e.target.value) })} />
          </Row>
          <Row label="Cooldown Minutes">
            <input className={inputClass()} type="number" value={settings.cooldown_minutes || 45} onChange={(e) => setSettings({ ...settings, cooldown_minutes: Number(e.target.value) })} />
          </Row>
        </div>

        <div className="mt-2 mb-5 rounded-xl border border-yellow-700/40 bg-yellow-950/10 p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-bold text-yellow-200">Market Scan Universe</div>
              <div className="text-xs text-gray-400 mt-1">
                Choose whether the Market Crypto Bot scans Robinhood's live tradable USD pairs or only your saved Crypto Watchlist.
              </div>
            </div>
            <span className="rounded-full border border-yellow-700/50 bg-black/50 px-3 py-1 text-xs text-yellow-200">
              {universeMode === 'robinhood_all' ? 'LIVE UNIVERSE' : 'WATCHLIST ONLY'}
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-3 mt-4">
            <button
              type="button"
              disabled={busy}
              onClick={() => setSettings({ ...settings, universe_mode: 'robinhood_all', scan_all: true })}
              className={`rounded-xl border px-4 py-3 text-left transition disabled:opacity-50 ${
                universeMode === 'robinhood_all'
                  ? 'border-green-500 bg-green-950/30 text-green-100'
                  : 'border-gray-700 bg-gray-950 text-gray-300 hover:border-yellow-700'
              }`}
            >
              <div className="font-bold">Live Robinhood Universe</div>
              <div className="mt-1 text-xs opacity-80">Default — fetch/filter Robinhood Trading Pairs, then rank with stochastic + MTF logic.</div>
            </button>

            <button
              type="button"
              disabled={busy}
              onClick={() => setSettings({ ...settings, universe_mode: 'watchlist_only', scan_all: false })}
              className={`rounded-xl border px-4 py-3 text-left transition disabled:opacity-50 ${
                universeMode === 'watchlist_only'
                  ? 'border-yellow-500 bg-yellow-950/30 text-yellow-100'
                  : 'border-gray-700 bg-gray-950 text-gray-300 hover:border-yellow-700'
              }`}
            >
              <div className="font-bold">Crypto Watchlist Only</div>
              <div className="mt-1 text-xs opacity-80">Restrict Market Bot scanning to only the pairs you save below.</div>
            </button>
          </div>

          {universeMode === 'robinhood_all' && (
            <div className="grid md:grid-cols-3 gap-3 mt-4">
              <Row label="Maximum Live Pairs">
                <input
                  className={inputClass()}
                  type="number"
                  min="10"
                  max="250"
                  value={settings.dynamic_universe_max_pairs || 100}
                  onChange={(e) => setSettings({ ...settings, dynamic_universe_max_pairs: Number(e.target.value) })}
                />
              </Row>
              <div className="rounded-lg border border-gray-800 bg-black/40 p-3">
                <div className="text-xs text-gray-500">Current source</div>
                <div className="mt-1 text-sm text-gray-200 break-words">{universeSource}</div>
              </div>
              <div className="rounded-lg border border-gray-800 bg-black/40 p-3">
                <div className="text-xs text-gray-500">Eligible live pairs</div>
                <div className="mt-1 text-lg font-bold text-yellow-200">{universeCount || 0}</div>
              </div>
            </div>
          )}

          {universeFallback && (
            <div className="mt-3 rounded-lg border border-amber-700/50 bg-amber-950/30 p-3 text-xs text-amber-200">
              Robinhood live-universe fallback is active. The backend is using the last-known-good universe or your saved watchlist until live pair discovery recovers.
            </div>
          )}

          {universe?.error && (
            <div className="mt-3 rounded-lg border border-red-700/50 bg-red-950/30 p-3 text-xs text-red-200">
              Live universe status could not be loaded: {universe.error}
            </div>
          )}

          {universeMode === 'robinhood_all' && universeSymbols.length > 0 && (
            <div className="mt-3 text-xs text-gray-400">
              Live examples: {universeSymbols.slice(0, 18).join(', ')}{universeSymbols.length > 18 ? ` +${universeSymbols.length - 18} more` : ''}
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => wrap(() => cryptoApi.getUniverse().then((r) => { setUniverse(r); return r; }), 'Robinhood trading-pair universe refreshed')}
              className="rounded-lg border border-yellow-700 px-3 py-2 text-xs text-yellow-200 hover:bg-yellow-500/10 disabled:opacity-50"
            >
              Refresh Live Universe
            </button>
            <div className="self-center text-[11px] text-gray-500">Universe-mode changes are saved when you click Save Crypto Bot.</div>
          </div>
        </div>

        <Row label={universeMode === 'robinhood_all' ? 'Crypto Watchlist (saved fallback / optional restriction)' : 'Crypto Watchlist (active scan list)'}>
          <textarea className={`${inputClass()} min-h-[90px]`} value={watchlistText} onChange={(e) => setWatchlistText(e.target.value)} />
        </Row>

        <div className="flex flex-wrap gap-3 mt-4">
          <button
            disabled={busy}
            className="rounded-lg bg-yellow-500 px-4 py-2 font-bold text-black disabled:opacity-50"
            onClick={() => wrap(
              () => cryptoApi.saveSettings({
                ...settings,
                universe_mode: universeMode,
                scan_all: universeMode === 'robinhood_all',
                dynamic_universe_max_pairs: Number(settings.dynamic_universe_max_pairs || 100),
                watchlist: asList(watchlistText),
              }),
              universeMode === 'robinhood_all'
                ? 'Crypto bot saved — Live Robinhood Universe is active'
                : 'Crypto bot saved — Crypto Watchlist Only is active'
            )}
          >
            Save Crypto Bot
          </button>
          <button disabled={busy} className="rounded-lg border border-yellow-500 px-4 py-2 text-yellow-200 disabled:opacity-50" onClick={() => wrap(() => cryptoApi.runTick({ execute: true, ignore_enabled: true }), 'Crypto bot tick ran')}>Run Crypto Bot Now</button>
          <button disabled={busy} className="rounded-lg border border-red-500 px-4 py-2 text-red-200 disabled:opacity-50" onClick={() => wrap(() => cryptoApi.runExits({ execute: true }), 'Crypto auto exits checked')}>Run Crypto Exits</button>
        </div>
      </div>

      <div className="rounded-2xl border border-yellow-700/40 bg-black/60 p-5">
        <h2 className="text-2xl font-bold text-yellow-300 mb-2">Targeted Crypto Bot</h2>
        <p className="text-sm text-gray-400 mb-4">Tracks one coin, waits for the buy setup, exits, then starts watching again.</p>
        <div className="grid md:grid-cols-3 gap-4">
          <Row label="Enabled">
            <select className={inputClass()} value={targetSettings.enabled ? '1' : '0'} onChange={(e) => setTargetSettings({ ...targetSettings, enabled: Number(e.target.value) })}>
              <option value="0">Off</option><option value="1">On</option>
            </select>
          </Row>
          <Row label="Coin / Pair">
            <input className={inputClass()} value={targetSettings.symbol || 'BTC-USD'} onChange={(e) => setTargetSettings({ ...targetSettings, symbol: e.target.value.toUpperCase() })} />
          </Row>
          <Row label="Broker">
            <select className={inputClass()} value={targetSettings.broker || 'paper'} onChange={(e) => setTargetSettings({ ...targetSettings, broker: e.target.value })}>
              <option value="paper">Paper / Test</option><option value="robinhood">Robinhood Crypto</option>
            </select>
          </Row>
          <Row label="Paper Mode">
            <select className={inputClass()} value={targetSettings.paper_mode ? '1' : '0'} onChange={(e) => setTargetSettings({ ...targetSettings, paper_mode: Number(e.target.value) })}>
              <option value="1">Paper only</option><option value="0">Live capable</option>
            </select>
          </Row>
          <Row label="Allow Live Orders">
            <select className={inputClass()} value={targetSettings.allow_live ? '1' : '0'} onChange={(e) => setTargetSettings({ ...targetSettings, allow_live: Number(e.target.value) })}>
              <option value="0">No</option><option value="1">Yes</option>
            </select>
          </Row>
          <Row label="Max Trade $">
            <input className={inputClass()} type="number" value={targetSettings.max_trade_usd || 25} onChange={(e) => setTargetSettings({ ...targetSettings, max_trade_usd: Number(e.target.value) })} />
          </Row>
          <Row label="Min Buy Score">
            <input className={inputClass()} type="number" value={targetSettings.min_score || 58} onChange={(e) => setTargetSettings({ ...targetSettings, min_score: Number(e.target.value) })} />
          </Row>
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <button disabled={busy} className="rounded-lg bg-yellow-500 px-4 py-2 font-bold text-black disabled:opacity-50" onClick={() => wrap(() => cryptoApi.saveTargetSettings(targetSettings), 'Targeted crypto settings saved')}>Save Targeted Crypto</button>
          <button disabled={busy} className="rounded-lg border border-yellow-500 px-4 py-2 text-yellow-200 disabled:opacity-50" onClick={() => wrap(() => cryptoApi.runTargetTick({ execute: true, ignore_enabled: true }), 'Targeted crypto tick ran')}>Run Targeted Now</button>
        </div>
      </div>

      <div className="rounded-2xl border border-yellow-700/40 bg-black/60 p-5">
        <h2 className="text-xl font-bold text-yellow-300 mb-3">Connect Robinhood Crypto</h2>
        <p className="text-sm text-gray-400 mb-3">Paste the Robinhood Crypto API key and base64 private key. They are saved encrypted by the backend.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Row label="Robinhood API Key"><input className={inputClass()} value={rhApiKey} onChange={(e) => setRhApiKey(e.target.value)} /></Row>
          <Row label="Base64 Private Key"><input className={inputClass()} type="password" value={rhPrivateKey} onChange={(e) => setRhPrivateKey(e.target.value)} /></Row>
        </div>
        <button disabled={busy} className="rounded-lg border border-yellow-500 px-4 py-2 text-yellow-200 disabled:opacity-50" onClick={() => wrap(() => cryptoApi.saveRobinhood({ api_key: rhApiKey, private_key_base64: rhPrivateKey }), 'Robinhood Crypto connected')}>Save Robinhood Crypto</button>
      </div>

      {message && <div className="rounded-xl border border-yellow-700/40 bg-yellow-500/10 p-3 text-yellow-200">{message}</div>}

      {top10.length > 0 && (
        <div className="rounded-2xl border border-yellow-700/40 bg-black/60 p-5 overflow-x-auto">
          <h3 className="text-xl font-bold text-yellow-300 mb-3">Top 10 Crypto Setups</h3>
          <table className="w-full text-sm text-left">
            <thead className="text-gray-400"><tr><th>Coin</th><th>Score</th><th>Price</th><th>RSI</th><th>15m</th><th>Reasons</th></tr></thead>
            <tbody>
              {top10.map((c) => (
                <tr key={c.symbol} className="border-t border-yellow-900/30">
                  <td className="py-2 text-yellow-200 font-bold">{c.symbol}</td>
                  <td>{c.score}</td>
                  <td>${Number(c.price || 0).toFixed(6)}</td>
                  <td>{Number(c.rsi || 0).toFixed(1)}</td>
                  <td>{Number(c.change_15m_pct || 0).toFixed(2)}%</td>
                  <td className="text-gray-400">{(c.reasons || []).slice(0, 3).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-yellow-700/40 bg-black/60 p-5">
          <h3 className="text-xl font-bold text-yellow-300 mb-3">Crypto Positions State</h3>
          <div className="space-y-2 text-sm">
            {states.length === 0 && <div className="text-gray-400">No crypto bot state yet.</div>}
            {states.map((s) => (
              <div key={`${s.bot_type}-${s.symbol}`} className="rounded-lg border border-yellow-900/30 p-3">
                <div className="font-bold text-yellow-200">{s.symbol} <span className="text-gray-400">({s.bot_type})</span></div>
                <div className="text-gray-300">In position: {s.in_position ? 'Yes' : 'No'} | Qty: {Number(s.qty || 0).toFixed(8)} | Entry: ${Number(s.avg_entry || 0).toFixed(6)} | High: ${Number(s.high_water || 0).toFixed(6)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-yellow-700/40 bg-black/60 p-5">
          <h3 className="text-xl font-bold text-yellow-300 mb-3">Crypto Bot Logs</h3>
          <div className="space-y-2 max-h-[420px] overflow-y-auto text-sm">
            {logs.length === 0 && <div className="text-gray-400">No crypto logs yet.</div>}
            {logs.map((l) => (
              <div key={l.id} className="rounded-lg border border-yellow-900/30 p-3">
                <div className="text-xs text-gray-500">{l.created_at} · {l.bot_type} · {l.symbol || 'all'}</div>
                <div className="text-gray-200">{l.message}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
